/**
 * The cart's displayed money figures: the SERVER's breakdown when the cart
 * has one, the cart's own arithmetic only as a marked estimate until then.
 *
 * Bug Intake #481 (https://trello.com/c/jWUmSkp4), gate round 1 P1: the cart
 * composed its Total in JS (StorefrontCart `cartCharge`), a twin of the
 * server's `compute_order_money`. The twin rounds the Stripe processing fee
 * to cents half-up BEFORE summing, the server sums and rounds once, so a card
 * store charging the processing fee on 15.00 of items showed 16.33 in the
 * cart and charged 16.32 (confirmation, email and order page all 16.32).
 * Gate round 3 put both sides on one rule (every component whole cents,
 * half-up; `@/utils/cartChargeCents`), so that case is now 16.33 on both,
 * held by the shared table in `cart-charge-parity.spec.js`.
 * Jake, 2026-10-22: one set of numbers on every surface.
 *
 * So the cart asks the server (`checkout_quote`, read-only) for THE
 * breakdown (`totals`, built by `api/src/pricing/order_totals.py`) and the
 * summary and the payment dialog render its figures. The JS figures remain
 * only for the moments the server has not answered for the cart's CURRENT
 * inputs (no address yet, a quote in flight, a failed quote); the components
 * mark them "(estimated)" so they never read as the final Total.
 */

/** True when `totals` has the shape `shopper_totals` returns. */
export function isServerTotals(totals) {
  return Boolean(
    totals &&
      typeof totals === "object" &&
      Array.isArray(totals.lines) &&
      Number.isFinite(Number(totals.total))
  );
}

function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** The amount of the server line `key`, or 0 when the server sent none. */
export function serverLineAmount(totals, key) {
  if (!isServerTotals(totals)) return 0;
  const line = totals.lines.find((l) => l && l.key === key);
  return line ? num(line.amount) : 0;
}

/** True when the server breakdown carries a line `key`. */
export function hasServerLine(totals, key) {
  return (
    isServerTotals(totals) && totals.lines.some((l) => l && l.key === key)
  );
}

/**
 * The figures a cart surface shows.
 *
 * `serverTotals` -- the `checkout_quote.totals` for the cart's current
 * inputs, or null. `fallback` -- the cart's own figures, keyed like the
 * result (`items`, `salesTax`, `taxAndFees`, `shipping`, `freeItem`,
 * `processingFee`, `serviceFee`, `subtotal`, `allowance`, `total`).
 *
 * Returns those keys plus `fromServer` (true when every figure is the
 * server's) and `minimumCharge` (the server's Stripe minimum-charge
 * adjustment line; 0 on the fallback, which has no such line).
 */
export function cartDisplayFigures(serverTotals, fallback = {}) {
  if (isServerTotals(serverTotals)) {
    return {
      fromServer: true,
      items: num(serverTotals.items),
      salesTax: num(serverTotals.sales_tax),
      taxAndFees: num(serverTotals.tax_and_fees),
      shipping: num(serverTotals.shipping),
      freeItem: num(serverTotals.free_item_discount),
      couponDiscount: num(serverTotals.coupon_discount),
      processingFee: num(serverTotals.processing_fee),
      serviceFee: num(serverTotals.service_fee),
      minimumCharge: serverLineAmount(serverTotals, "minimum_charge"),
      subtotal: num(serverTotals.subtotal),
      allowance: num(serverTotals.allowance),
      total: num(serverTotals.total),
    };
  }
  return {
    fromServer: false,
    items: num(fallback.items),
    salesTax: num(fallback.salesTax),
    taxAndFees: num(fallback.taxAndFees),
    shipping: num(fallback.shipping),
    freeItem: num(fallback.freeItem),
    couponDiscount: 0,
    processingFee: num(fallback.processingFee),
    serviceFee: num(fallback.serviceFee),
    minimumCharge: 0,
    subtotal: num(fallback.subtotal),
    allowance: num(fallback.allowance),
    total: num(fallback.total),
  };
}

/**
 * The key identifying the inputs one `checkout_quote` answer is valid for.
 * A quote answers the cart only while this key is unchanged: any edit to the
 * lines, the shipping figure, the coupon, the department, the tax location or
 * the verified allowance holder makes the stored answer stale, and the cart falls back to its estimate
 * until the next answer lands.
 */
export function cartQuoteKey({
  storeId,
  lines = [],
  itemsTotal = 0,
  shippingCost = 0,
  shippingQuoteId = null,
  couponCode = null,
  departmentId = null,
  location = null,
  holderEmail = null,
} = {}) {
  return JSON.stringify({
    s: storeId == null ? null : String(storeId),
    l: lines.map((line) => [
      line && line.id != null ? line.id : null,
      num(line && line.quantity),
      num(line && line.price),
    ]),
    t: num(itemsTotal),
    sh: num(shippingCost),
    q: shippingQuoteId == null ? null : String(shippingQuoteId),
    c: couponCode || null,
    d: departmentId == null ? null : String(departmentId),
    // The verified address that holds the allowance on an email-domain
    // store (card #596 follow-up): verifying changes the draw.
    h: holderEmail || null,
    loc: location
      ? [
          location.street_and_number || "",
          location.city || "",
          location.region || "",
          location.zip_code || "",
          location.country || "",
          Boolean(location.isCompanyLocation),
        ]
      : null,
  });
}
